import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Add } from '@mint-lib/icons';
import {
  Button,
  Input,
  InputHelperText,
  InputLabel,
  InputRoot,
} from '@mint-lib/tw-components';
import { Alert } from '@mint-lib/tw-components/patterns';
import { z } from 'zod';

import { MAX_AMOUNT_OF_EMAILS } from '../../../constants/Emails.js';

type Email = {
  id: number;
  email: string;
};

type Props = {
  onAdd: (value: string) => void;
  onError?: (message: string) => void;
  emails: Email[];
};

const EmailForm = ({
  onError = () => null,
  onAdd = () => null,
  emails,
}: Props) => {
  const { t } = useTranslation('@myn/integrations_v2');

  const [value, setValue] = useState<string>();
  const [error, setError] = useState<string>();

  const isFull = useMemo(
    () => emails.length === MAX_AMOUNT_OF_EMAILS,
    [emails.length],
  );

  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setValue(event.target.value);
      if (error) {
        setError('');
      }
    },
    [error],
  );

  const handleAddEmail = useCallback(() => {
    const emailValidatorZod = z.email().trim();

    if (!value?.trim() || !emailValidatorZod.safeParse(value).success) {
      setError(t('Invalid e-mail address'));
      return;
    }

    if (emails.find((eValue) => eValue.email === value)) {
      setError(t('This e-mail address is already added'));
      return;
    }

    onAdd(value);
    setValue('');
  }, [onError, onAdd, value]);

  return (
    <div className="mb-4 flex items-end">
      {isFull ? (
        <Alert
          severity="info"
          title={t('You reached the maximum number of e-mail addresses.')}
          variant="inline"
          removable={false}
        />
      ) : (
        <div className="flex w-full flex-row items-end gap-3">
          <InputRoot className="w-full">
            <InputLabel htmlFor="email" label={t('Email')} />
            <Input
              id="email"
              onChange={handleChange}
              placeholder={t('Enter e-mail address')}
              value={value}
              type="email"
              color={error ? 'error' : 'default'}
              className="w-full"
            />
            {!!error && <InputHelperText color="error" helperText={error} />}
          </InputRoot>
          <div className={error ? 'mb-5' : ''}>
            <Button
              color="secondary"
              size="large"
              disabled={isFull || !!error}
              onClick={handleAddEmail}
              data-testid="eform-addEmail"
              data-dd-action-name="add email"
            >
              <Add />
              {t('Add email')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailForm;
